import IconDownChevron from '../icons/IconDownChevron';
import IconCalendar from '../icons/IconCalendar';
import { useTranslation } from 'react-i18next';
import { DatePickerTable } from './DatePickerTable';
import { useEffect, useRef, useState } from 'react';
import { formatDate, getDateKeyInTimezone, isSameDayInVenueTimezone } from '../helpers/formatters';
import { useGetTimeslots } from '../../openapi/timeslots/timeslots.js';
import { useGetShow } from '../../openapi/shows/shows.js';
import { ITimeslot, useCart } from '../hooks/useCart.js';
import { parseCancellationPreset } from '../helpers/cancellationReason';
import { getSlotTemplate, resolveSlotLabel, slotOrderIndex } from '../helpers/slotTemplate';
import { useLang } from '../hooks/useLanguage';

function ShowDatePicker({
    showId,
    location,
    rentalMode = false,
}: {
    showId: number;
    location: string;
    rentalMode?: boolean;
}) {
    const { timeslot: selectedTimeslot, setTimeslot, setCart } = useCart();
    const { data: { timeslots } = { timeslots: [] } } = useGetTimeslots({
        optionalId: showId,
        onlyCurrentAndFuture: true,
        applyBookingWindow: true,
        pageSize: 1000,
    });
    // Slot labels/ordering come from the show's rental slot template. react-query
    // dedupes this with the parent's show fetch, so it costs nothing extra.
    const { data: show } = useGetShow(showId, { query: { enabled: !!showId } });
    const slotTemplate = getSlotTemplate(show);
    const { t } = useTranslation();
    const { getI18n } = useLang();
    const [multipleEventsOnSameDay, setMultipleEventsOnSameDay] = useState<ITimeslot[] | false>(
        false,
    );

    // useEffect to set the first day's timeslot(s) only once, on initial mount
    const hasAutoSelected = useRef(false);
    useEffect(() => {
        if (!timeslots || timeslots.length === 0) return;

        // Returning to this screen with a timeslot already chosen (e.g. via
        // "Choose a different date or time") remounts it with multipleEventsOnSameDay
        // reset. Recompute the selected day's events so the rental period picker
        // shows again instead of the static start-time line. Rental-only: non-rental
        // multi-event shows keep their original static line on return.
        if (rentalMode && selectedTimeslot) {
            const selectedDayKey = getDateKeyInTimezone(selectedTimeslot.startTime);
            const eventsOnSelectedDay = timeslots.filter(
                (e) => getDateKeyInTimezone(e.startTime) === selectedDayKey,
            );
            setMultipleEventsOnSameDay(
                eventsOnSelectedDay.length > 1
                    ? eventsOnSelectedDay.map((e) => ({
                          id: e.id as unknown as number,
                          startTime: e.startTime,
                          activeArrangementId: e.activeArrangementId ?? null,
                          slotKey: e.slotKey ?? null,
                      }))
                    : false,
            );
            return;
        }

        // First visit with nothing chosen yet: auto-select the first available day
        // once. The !selectedTimeslot guard matters on a non-rental remount (the
        // rental branch above already returned): the cart keeps a selected day, so
        // without it auto-select would overwrite the guest's chosen date.
        if (!hasAutoSelected.current && !selectedTimeslot) {
            hasAutoSelected.current = true;
            const orderedEvents = timeslots
                // Rental slots may have started already and still be sellable
                // (the backend floors the list to the venue-local day), so only
                // non-rental shows drop past start times here.
                .filter((e) => rentalMode || new Date(e.startTime) >= new Date())
                .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

            if (orderedEvents.length > 0) {
                const firstDayKey = getDateKeyInTimezone(orderedEvents[0].startTime);
                const eventsOnFirstDay = orderedEvents.filter(
                    (e) => getDateKeyInTimezone(e.startTime) === firstDayKey,
                );

                if (eventsOnFirstDay.length === 1) {
                    setTimeslot({
                        id: eventsOnFirstDay[0].id as unknown as number,
                        startTime: eventsOnFirstDay[0].startTime,
                        activeArrangementId: eventsOnFirstDay[0].activeArrangementId ?? null,
                        slotKey: eventsOnFirstDay[0].slotKey ?? null,
                    });
                    setMultipleEventsOnSameDay(false);
                } else if (eventsOnFirstDay.length > 1) {
                    // multiple events on the same day
                    setMultipleEventsOnSameDay(
                        eventsOnFirstDay.map((e) => ({
                            id: e.id as unknown as number,
                            startTime: e.startTime,
                            activeArrangementId: e.activeArrangementId ?? null,
                            slotKey: e.slotKey ?? null,
                        })),
                    );
                    setTimeslot({
                        id: eventsOnFirstDay[0].id as unknown as number,
                        startTime: eventsOnFirstDay[0].startTime,
                        activeArrangementId: eventsOnFirstDay[0].activeArrangementId ?? null,
                        slotKey: eventsOnFirstDay[0].slotKey ?? null,
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeslots, selectedTimeslot]);

    return (
        <>
            <div className="fireknife-datepicker">
                {location === 'main' && (
                    <div className="fireknife-datepicker__title-bar">
                        <IconCalendar className="fireknife-datepicker__title-icon" />
                        <div className="fireknife-datepicker__title-bar-text">
                            {t('widgetDatePicker.dateAndTime')}
                        </div>
                    </div>
                )}
                <DatePickerTable
                    localeStr={t('widgetLocale')}
                    dayRenderer={(day) => {
                        const eventsOnThisDay =
                            timeslots?.filter((e) =>
                                isSameDayInVenueTimezone(e.startTime, day.date),
                            ) || [];
                        const isSelected =
                            selectedTimeslot &&
                            isSameDayInVenueTimezone(selectedTimeslot.startTime, day.date);

                        // A day is treated as cancelled only when every timeslot on
                        // it is cancelled; a day that still has a bookable slot must
                        // not look unbookable.
                        const cancelledEvents = eventsOnThisDay.filter((e) =>
                            e.cancellationReason?.trim(),
                        );
                        const isFullyCancelled =
                            eventsOnThisDay.length > 0 &&
                            cancelledEvents.length === eventsOnThisDay.length;
                        // Optional per-reason color override; absent it, the
                        // __day-cancelled class still differentiates the day.
                        const cancelledColor = isFullyCancelled
                            ? cancelledEvents
                                  .map((e) => parseCancellationPreset(e.cancellationReason)?.color)
                                  .find(Boolean)
                            : undefined;

                        const classNames = [
                            'fireknife-datepicker__day',
                            day.inCurrentMonth ? 'fireknife-datepicker__day-current-month' : '',
                            eventsOnThisDay.length >= 1
                                ? 'fireknife-datepicker__day-highlighted'
                                : '',
                            isFullyCancelled ? 'fireknife-datepicker__day-cancelled' : '',
                            isSelected ? 'fireknife-datepicker__day-selected' : '',
                        ];
                        const cancelledStyle = cancelledColor
                            ? isSelected
                                ? {
                                      backgroundColor: cancelledColor,
                                      borderColor: cancelledColor,
                                      color: '#fff',
                                  }
                                : {
                                      borderColor: cancelledColor,
                                      color: cancelledColor,
                                  }
                            : undefined;

                        return (
                            <td
                                key={day.date.toString()}
                                style={cancelledStyle}
                                onClick={() => {
                                    if (eventsOnThisDay.length === 0) {
                                        return;
                                    }

                                    // if we only have one event, that's it, select it
                                    if (eventsOnThisDay.length === 1) {
                                        setTimeslot({
                                            // force cast chain for some reason
                                            id: eventsOnThisDay[0].id as unknown as number,
                                            startTime: eventsOnThisDay[0].startTime,
                                            activeArrangementId:
                                                eventsOnThisDay[0].activeArrangementId ?? null,
                                            slotKey: eventsOnThisDay[0].slotKey ?? null,
                                        });
                                        setMultipleEventsOnSameDay(false);
                                        return;
                                    }

                                    // multiple events on the same day
                                    setMultipleEventsOnSameDay(
                                        eventsOnThisDay.map((e) => ({
                                            // force cast chain for some reason
                                            id: e.id as unknown as number,
                                            startTime: e.startTime,
                                            activeArrangementId: e.activeArrangementId ?? null,
                                            slotKey: e.slotKey ?? null,
                                        })),
                                    );
                                    setTimeslot(null);
                                }}
                                className={classNames.filter((e) => e).join(' ')}
                            >
                                {day.date.getDate()}
                            </td>
                        );
                    }}
                />
                {multipleEventsOnSameDay &&
                    rentalMode &&
                    (() => {
                        // Order the day's slots by the show's template; slots not in
                        // the template fall to the end (by startTime as a tiebreak).
                        const slotTimeslots = [...multipleEventsOnSameDay].sort((a, b) => {
                            const oa = slotOrderIndex(slotTemplate, a.slotKey);
                            const ob = slotOrderIndex(slotTemplate, b.slotKey);
                            if (oa !== ob) return oa - ob;
                            return (
                                new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
                            );
                        });
                        return (
                            <div className="fireknife-datepicker__multiple-events">
                                <label className="fireknife-datepicker__timeslot-label">
                                    {t('widgetDatePicker.selectPeriod')}
                                </label>
                                <div className="fireknife-datepicker__period-buttons">
                                    {slotTimeslots.map((ts) => (
                                        <button
                                            key={ts.id}
                                            type="button"
                                            className={[
                                                'fireknife-datepicker__period-button',
                                                selectedTimeslot?.id === ts.id
                                                    ? 'fireknife-datepicker__period-button--selected'
                                                    : '',
                                                slotTemplate.find(
                                                    (slot) => slot.key === ts.slotKey,
                                                )?.isFullDay
                                                    ? 'fireknife-datepicker__period-button--full-day'
                                                    : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            onClick={() => {
                                                if (ts.slotKey !== selectedTimeslot?.slotKey) {
                                                    setCart([]);
                                                }
                                                setTimeslot({
                                                    id: ts.id,
                                                    startTime: ts.startTime,
                                                    activeArrangementId:
                                                        ts.activeArrangementId ?? null,
                                                    slotKey: ts.slotKey ?? null,
                                                });
                                            }}
                                        >
                                            {resolveSlotLabel(slotTemplate, ts, getI18n) ||
                                                formatDate(ts.startTime, t('widgetLocale'))}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                {multipleEventsOnSameDay && !rentalMode && (
                    <div className="fireknife-datepicker__multiple-events">
                        <label
                            className="fireknife-datepicker__timeslot-label"
                            htmlFor="fireknife-timeslot-select"
                        >
                            {t('widgetDatePicker.multipleEventsOnSameDay')}
                        </label>
                        <div className="fireknife-form-control-box">
                            <select
                                id="fireknife-timeslot-select"
                                className="fireknife-datepicker__timeslot-select fireknife-form-control fireknife-form-control--select"
                                value={selectedTimeslot?.id}
                                onChange={(e) => {
                                    const selected = multipleEventsOnSameDay.find(
                                        (event) => event.id === parseInt(e.target.value, 10),
                                    );
                                    if (selected) {
                                        setTimeslot({
                                            id: selected.id,
                                            startTime: selected.startTime,
                                            activeArrangementId:
                                                selected.activeArrangementId ?? null,
                                        });
                                    }
                                }}
                            >
                                <option value="">{t('widgetDatePicker.emptyOption')}</option>
                                {multipleEventsOnSameDay?.map((timeslot, index) => (
                                    <option
                                        key={index}
                                        value={timeslot.id}
                                        selected={timeslot.id === selectedTimeslot?.id}
                                    >
                                        {formatDate(timeslot.startTime, t('widgetLocale'))}
                                    </option>
                                ))}
                            </select>
                            <IconDownChevron className="fireknife-datepicker__timeslot-select-icon" />
                        </div>
                    </div>
                )}
                {selectedTimeslot && !multipleEventsOnSameDay && (
                    <div className="fireknife-datepicker__multiple-events">
                        <label
                            className="fireknife-datepicker__timeslot-label"
                            htmlFor="fireknife-timeslot-select"
                        >
                            {t('widgetDatePicker.selectedTimeslot')}
                        </label>
                        <span className="fireknife-datepicker__selected-timeslot-value">
                            {formatDate(selectedTimeslot.startTime, t('widgetLocale'))}
                        </span>
                    </div>
                )}
            </div>
        </>
    );
}

export default ShowDatePicker;
